import React, { ReactNode } from 'react';
import { ColumnsType } from 'antd/lib/table';
import {
  OptionProps,
  OverdueTaskRow,
  ReportsFilters,
  ReportsSortBy,
} from '@models/interfaces';
import {
  getOverdueSortableColumn,
  getSeverityClassName,
  renderDisplayValue,
  renderValue,
} from './common';

export const getOverdueTaskColumns = (
  filters: ReportsFilters,
  sortByOptions: OptionProps[],
): ColumnsType<OverdueTaskRow> => [
  getOverdueSortableColumn(
    'projectBundleID',
    'Project Bundle ID',
    ReportsSortBy.ProjectBundleID,
    filters,
    sortByOptions,
    renderValue,
  ),
  {
    dataIndex: 'fuzeIDsDisplay',
    key: 'fuzeIDsDisplay',
    render: (_value: unknown, record: OverdueTaskRow): ReactNode =>
      renderDisplayValue(record.fuzeIDsDisplay, record.fuzeIDs),
    title: 'Fuze IDs',
  },
  {
    dataIndex: 'psProjectNumbersDisplay',
    key: 'psProjectNumbersDisplay',
    render: (_value: unknown, record: OverdueTaskRow): ReactNode =>
      renderDisplayValue(
        record.psProjectNumbersDisplay,
        record.psProjectNumbers,
      ),
    title: 'PS Project Numbers',
  },
  {
    dataIndex: 'paceTaskID',
    key: 'paceTaskID',
    render: renderValue,
    title: 'PACE Task ID',
  },
  getOverdueSortableColumn(
    'taskName',
    'Task Name',
    ReportsSortBy.TaskName,
    filters,
    sortByOptions,
    renderValue,
  ),
  {
    dataIndex: 'paceTaskScreensDisplay',
    key: 'paceTaskScreensDisplay',
    render: (_value: unknown, record: OverdueTaskRow): ReactNode =>
      renderDisplayValue(record.paceTaskScreensDisplay, record.paceTaskScreens),
    title: 'PACE Task Module(s)',
  },
  {
    dataIndex: 'projectStatus',
    key: 'projectStatus',
    render: renderValue,
    title: 'Project Status',
  },
  {
    dataIndex: 'progressStatus',
    key: 'progressStatus',
    render: renderValue,
    title: 'Progress Status',
  },
  getOverdueSortableColumn(
    'forecastedDate',
    'Forecasted Date',
    ReportsSortBy.ForecastDate,
    filters,
    sortByOptions,
    renderValue,
  ),
  {
    dataIndex: 'PACEForecastedDate',
    key: 'PACEForecastedDate',
    render: renderValue,
    title: 'PACE Forecasted Date',
  },
  {
    dataIndex: 'actualDate',
    key: 'actualDate',
    render: renderValue,
    title: 'Actual Date',
  },
  {
    dataIndex: 'PACEActualDate',
    key: 'PACEActualDate',
    render: renderValue,
    title: 'PACE Actual Date',
  },
  getOverdueSortableColumn(
    'daysOverdue',
    'Days Overdue',
    ReportsSortBy.DaysOverdue,
    filters,
    sortByOptions,
    (value: unknown): ReactNode =>
      React.createElement(
        'span',
        { className: 'prov-overdue-tasks-report__days' },
        renderValue(value as number),
      ),
  ),
  getOverdueSortableColumn(
    'assignedAgent',
    'Assigned Agent',
    ReportsSortBy.AssignedAgent,
    filters,
    sortByOptions,
    renderValue,
  ),
  getOverdueSortableColumn(
    'siteName',
    'Site Name',
    ReportsSortBy.SiteName,
    filters,
    sortByOptions,
    renderValue,
  ),
  {
    dataIndex: 'psLocationCode',
    key: 'psLocationCode',
    render: renderValue,
    title: 'PS Location Code',
  },
  {
    dataIndex: 'mdgLocationCode',
    key: 'mdgLocationCode',
    render: renderValue,
    title: 'MDG Location Code',
  },
];

export const getOverdueReportRowClassName = (record: OverdueTaskRow): string =>
  [
    getSeverityClassName(record.daysOverdue),
    record.projectBundleID
      ? 'prov-overdue-tasks-report__row--clickable'
      : 'prov-overdue-tasks-report__row--disabled',
  ].join(' ');

export const getOverdueReportRowKey = (record: OverdueTaskRow): string =>
  record.projectPaceTaskDataId ||
  [
    record.projectId,
    record.projectBundleID,
    record.paceTaskId,
    record.forecastedDate,
  ]
    .filter(Boolean)
    .join('-');
